import { useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { useEntityDoc, createEntity, updateEntity } from '../data/entities';
import { isValidPlanType } from './planTypes';

const LEVELS = new Set(['beginner', 'intermediate', 'advanced']);

/**
 * Live profile for the signed-in user (doc id = uid).
 * `profile` is undefined while loading, null when missing, else the doc.
 */
export function useUserProfile() {
  const { user } = useAuth();
  const uid = user && !user.isAnonymous ? user.uid : null;
  const { doc: profile, error } = useEntityDoc('userProfile', {}, uid);

  const savePlanType = useCallback(
    async (planType) => {
      if (!uid) throw new Error('Sign in to choose a plan');
      if (!isValidPlanType(planType)) throw new Error('Unknown plan type');
      if (profile) {
        await updateEntity('userProfile', {}, uid, { planType });
      } else {
        await createEntity('userProfile', {}, { planType }, { id: uid });
      }
    },
    [uid, profile],
  );

  const saveProfileFields = useCallback(
    async (fields) => {
      if (!uid) throw new Error('Sign in to save your profile');
      if (!profile?.planType) throw new Error('Choose a plan first');
      const patch = {};
      if (fields.displayName !== undefined) {
        patch.displayName = String(fields.displayName || '').trim().slice(0, 80);
      }
      if (fields.practiceLevel !== undefined) {
        const lv = String(fields.practiceLevel || '');
        if (!LEVELS.has(lv)) throw new Error('Invalid practice level');
        patch.practiceLevel = lv;
      }
      if (fields.goals !== undefined) {
        patch.goals = String(fields.goals || '').trim().slice(0, 400);
      }
      if (fields.preferredMins !== undefined) {
        const n = Number(fields.preferredMins);
        if (!Number.isFinite(n)) throw new Error('Invalid preferred minutes');
        patch.preferredMins = Math.max(5, Math.min(120, Math.round(n)));
      }
      if (fields.equipment !== undefined) {
        patch.equipment = String(fields.equipment || '').trim().slice(0, 400);
      }
      await updateEntity('userProfile', {}, uid, patch);
    },
    [uid, profile],
  );

  return {
    uid,
    profile,
    planType: profile?.planType || null,
    // On fetch failure `profile` stays undefined — don't spin forever.
    loading: !!uid && profile === undefined && !error,
    error,
    savePlanType,
    saveProfileFields,
  };
}
